import { useEffect, useId, useState, type ReactNode } from "react";
import { mascararMoeda, moedaDeNumero } from "../lib/mascaras";
import { emPercentual, emReais } from "../lib/montarCalculo";

// As peças das três Calculadoras da Reforma (000221).
//
// Elas moram juntas porque as três telas compartilham o mesmo vocabulário
// visual — o campo de moeda, o selo de "dados simulados", o painel de resultado
// e a caixa de recusa do gov —, e porque a decisão mais delicada delas é a
// mesma: **dizer, ao lado de cada número, o quanto ele vale**. O piloto da
// Receita responde com alíquotas que em boa parte dos cenários ainda não foram
// fixadas em lei, e um número desses apresentado sem selo é um número que vai
// para um cliente.
//
// A forma é FORMULÁRIO + botão, e não a `BancadaDeCalculo` das quatro
// calculadoras do Radar. A bancada existe porque lá o gesto é mexer num número
// e ver o outro mudar, o que só funciona quando a conta é local e instantânea.
// Aqui cada resultado custa uma ida à API da Receita: um controle deslizante
// dispararia dezenas de chamadas por arrasto, e o número só apareceria depois
// de soltar — ou seja, a peça prometeria uma resposta ao vivo que não existe.

// ---------------------------------------------------------------------------
// Os campos
// ---------------------------------------------------------------------------
/** As classes do campo dentro do painel navy — onde cor semântica é armadilha. */
export const CAMPO_NAVY =
  "h-12 w-full rounded-xl border border-navy-mute/30 bg-white/5 px-4 text-base text-white placeholder:text-navy-mute/70 focus:border-laranja focus:ring-3 focus:ring-laranja/25 focus:outline-none";

/** As classes do campo sobre a superfície clara. */
export const CAMPO_CLARO =
  "h-11 w-full rounded-xl border-[1.5px] border-borda bg-superficie px-3 text-tinta transition placeholder:text-cinza/60 focus:border-laranja focus:ring-3 focus:ring-laranja/20 focus:outline-none";

export function Rotulo({
  children,
  navy = false,
  para,
}: {
  children: ReactNode;
  navy?: boolean;
  para?: string;
}) {
  return (
    <label
      htmlFor={para}
      className={
        navy
          ? "mb-1.5 block text-xs font-bold tracking-widest text-navy-mute uppercase"
          : "mb-1.5 block text-xs font-semibold text-cinza"
      }
    >
      {children}
    </label>
  );
}

/**
 * O campo de dinheiro.
 *
 * Duas coisas separadas, e é a cicatriz do formulário público: o TEXTO que
 * aparece ("R$ 1.234,56") e o NÚMERO que viaja (1234.56). A máscara é
 * apresentação; mandar o texto mascarado ao servidor grava nulo em silêncio.
 * A conversão acontece aqui, na fronteira, e nunca na montagem do pedido.
 */
export function CampoMoeda({
  valor,
  aoMudar,
  navy = false,
  id,
  placeholder = "R$ 0,00",
}: {
  valor: number | null;
  aoMudar: (n: number | null) => void;
  navy?: boolean;
  id?: string;
  placeholder?: string;
}) {
  const [texto, setTexto] = useState(() => moedaDeNumero(valor));

  // O campo tem DOIS estados — o texto mascarado que aparece e o número que
  // viaja —, e quem manda é o de fora. Sem esta sincronização, limpar os
  // valores no pai deixa o texto antigo na tela: o aluno vê "R$ 1.000,00" num
  // campo cujo valor **não vai** no pedido.
  //
  // Achado pela prova de tela em 14/09, no caso 9 (trocar de aba na Base de
  // Cálculo limpa os componentes). É a cicatriz do `useState`/`useEffect` da
  // tela de parâmetros pelo avesso: lá o efeito consertava o que o estado
  // inicial errava, aqui faltava o efeito e o estado inicial ficava para
  // sempre.
  //
  // A guarda é comparar NÚMERO com NÚMERO, e não texto com texto: enquanto o
  // aluno digita, o pai devolve exatamente o número que o texto representa, os
  // dois batem e o efeito não faz nada — reformatar a cada tecla moveria o
  // cursor dele.
  useEffect(() => {
    if (mascararMoeda(texto).numero === valor) return;
    setTexto(moedaDeNumero(valor));
    // `texto` fica de propósito FORA da lista de dependências: ele é o que este
    // efeito escreve, e incluí-lo faria o efeito reagir à própria escrita.
  }, [valor]);

  return (
    <input
      id={id}
      className={`${navy ? CAMPO_NAVY : CAMPO_CLARO} tabular-nums`}
      inputMode="numeric"
      value={texto}
      placeholder={placeholder}
      onChange={(e) => {
        const m = mascararMoeda(e.target.value);
        setTexto(m.texto);
        aoMudar(m.numero);
      }}
    />
  );
}

/**
 * O campo de percentual.
 *
 * Aceita vírgula e ponto de propósito: quem digita alíquota escreve "8,8" no
 * Brasil e "8.8" ao copiar de um documento. Recusar um dos dois faria o aluno
 * perder o cálculo num detalhe de teclado.
 */
export function CampoPercentual({
  valor,
  aoMudar,
  navy = false,
  id,
  placeholder = "0,00",
}: {
  valor: number | null;
  aoMudar: (n: number | null) => void;
  navy?: boolean;
  id?: string;
  placeholder?: string;
}) {
  const [texto, setTexto] = useState(() => (valor === null ? "" : String(valor).replace(".", ",")));

  // A mesma sincronização do `CampoMoeda`, e pelo mesmo motivo: o botão "Usar a
  // estimativa" das alíquotas nominais escreve os três valores DE FORA, e sem
  // isto os campos continuariam vazios enquanto o pedido levaria os números.
  useEffect(() => {
    // "8," no meio da digitação vira 8, que é o valor que o pai já tem — então
    // os dois batem e o efeito não apaga a vírgula recém-digitada.
    const meu = texto.trim() === "" ? null : Number(texto.replace(",", "."));
    if (meu === valor) return;
    setTexto(valor === null ? "" : String(valor).replace(".", ","));
  }, [valor]);

  return (
    <div className="relative">
      <input
        id={id}
        className={`${navy ? CAMPO_NAVY : CAMPO_CLARO} pr-9 tabular-nums`}
        inputMode="decimal"
        value={texto}
        placeholder={placeholder}
        onChange={(e) => {
          const bruto = e.target.value.replace(/[^\d,.]/g, "");
          setTexto(bruto);
          const n = Number(bruto.replace(",", "."));
          aoMudar(bruto.trim() === "" || !Number.isFinite(n) ? null : n);
        }}
      />
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm ${navy ? "text-navy-mute" : "text-cinza"}`}
      >
        %
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Os avisos
// ---------------------------------------------------------------------------
/**
 * O selo de "este número é simulado".
 *
 * A família é **alerta** (laranja), e não `aviso` — nesta casa `aviso` é VERDE,
 * a cor de confirmação, e um "atenção, a alíquota não existe em lei" pintado de
 * verde comunica o oposto exato da mensagem. A cicatriz é do Leitor de
 * Processos, cujo aviso mais importante saiu em verde-esmeralda.
 *
 * E o texto vem do CÓDIGO que o gov mandou (1 a 6), não de uma frase nossa:
 * "simulado" sozinho não diz ao contador o que ele pode usar, e a diferença
 * entre "a alíquota deste cClassTrib ainda não foi definida" e "o cálculo usou
 * as alíquotas que VOCÊ digitou" é exatamente o que ele precisa saber.
 */
export function SeloSimulado({ codigo, texto }: { codigo: number | null; texto: string | null }) {
  if (codigo === null) return null;
  return (
    <div data-selo-simulado={codigo} className="rounded-xl border border-alerta-tinta/40 bg-alerta-fundo p-3">
      <p className="text-xs leading-relaxed font-semibold text-alerta-tinta">
        Números simulados — não use este resultado como apuração definitiva.
      </p>
      {texto && <p className="mt-1 text-xs leading-relaxed text-alerta-tinta">{texto}</p>}
    </div>
  );
}

/**
 * O gov recusou os dados: o título e o detalhe são DELE.
 *
 * As mensagens do piloto são escritas para quem preenche ("Dados do Imposto
 * Seletivo devem ser informados para NCM 24021000 e data 2026-06-01") e dizem o
 * NCM e a data que o aluno digitou. Trocá-las por "verifique os dados"
 * apagaria a única frase que diz o que fazer.
 */
export function RecusaDoGov({ titulo, detalhe }: { titulo: string; detalhe: string }) {
  return (
    <div data-recusa="gov" className="rounded-cf-lg border border-erro-borda bg-erro-fundo p-4" role="alert">
      <p className="text-sm font-bold text-erro-tinta">{titulo}</p>
      <p className="mt-1 text-sm leading-relaxed text-erro-tinta">{detalhe}</p>
      <p className="mt-2 text-xs leading-relaxed text-erro-tinta/80">
        Esta mensagem é da calculadora da Receita Federal. Corrija o campo que ela cita e
        calcule de novo.
      </p>
    </div>
  );
}

/** O piloto não respondeu. Nada a corrigir no formulário — é esperar. */
export function GovForaDoAr({ motivo }: { motivo: string }) {
  const explica =
    motivo === "tempo"
      ? "A calculadora da Receita demorou demais para responder."
      : motivo === "rede"
        ? "Não conseguimos alcançar a calculadora da Receita."
        : motivo.startsWith("gov-")
          ? "A calculadora da Receita respondeu com um erro interno."
          : "A calculadora da Receita não respondeu como esperado.";
  return (
    <div data-indisponivel={motivo} className="rounded-cf-lg border border-alerta-tinta/40 bg-alerta-fundo p-4" role="alert">
      <p className="text-sm font-bold text-alerta-tinta">{explica}</p>
      <p className="mt-1 text-sm leading-relaxed text-alerta-tinta">
        Os seus dados continuam preenchidos aqui. Tente calcular de novo em alguns
        minutos — o ambiente de piloto da Receita sai do ar de vez em quando, e não há
        nada a corrigir no formulário.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// O resultado
// ---------------------------------------------------------------------------
/** O número grande, com o rótulo em cima e o apoio embaixo. */
export function NumeroHeroi({
  rotulo,
  valor,
  apoio,
}: {
  rotulo: string;
  valor: ReactNode;
  apoio?: ReactNode;
}) {
  return (
    <div data-heroi={rotulo} className="rounded-cf-lg bg-tile p-5">
      <p className="text-xs font-bold tracking-widest text-cinza uppercase">{rotulo}</p>
      <p data-heroi-valor className="mt-1 font-display text-3xl font-bold text-titulo tabular-nums sm:text-4xl">
        {valor}
      </p>
      {apoio && <p className="mt-1 text-xs leading-relaxed text-cinza">{apoio}</p>}
    </div>
  );
}

/**
 * A linha de um tributo: rótulo, alíquota e valor.
 *
 * A memória de cálculo do gov fica atrás de um `<details>`, e não some: ela é
 * uma frase longa que explica o enquadramento legal e a base usada, e é
 * exatamente o que o aluno da Pós precisa para conferir. Mostrá-la sempre
 * empurraria o resultado para fora da tela; escondê-la de vez tiraria a única
 * coisa que transforma "R$ 9,00" em aula.
 */
export function LinhaDeTributo({
  rotulo,
  aliquota,
  valor,
  memoria,
}: {
  rotulo: string;
  aliquota: number | null;
  valor: number | null;
  memoria?: string | null;
}) {
  const id = useId();
  return (
    <div data-tributo={rotulo} className="border-b border-borda py-3 last:border-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-tinta">{rotulo}</span>
        <span className="flex items-baseline gap-3">
          <span data-aliquota className="text-xs text-cinza tabular-nums">{emPercentual(aliquota)}</span>
          <span data-valor className="text-base font-bold text-titulo tabular-nums">{emReais(valor)}</span>
        </span>
      </div>
      {memoria && (
        <details className="mt-1">
          <summary className="cursor-pointer text-xs text-link hover:underline" id={id}>
            Como a Receita chegou a este valor
          </summary>
          <p className="mt-1.5 rounded-lg bg-superficie-2 p-3 text-xs leading-relaxed text-cinza">
            {memoria}
          </p>
        </details>
      )}
    </div>
  );
}

/** A moldura de um bloco do formulário. */
export function Bloco({
  titulo,
  descricao,
  children,
  acao,
}: {
  titulo: string;
  descricao?: ReactNode;
  children: ReactNode;
  acao?: ReactNode;
}) {
  return (
    <section className="rounded-cf-lg border border-borda bg-superficie p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-base font-bold text-titulo">{titulo}</h2>
          {descricao && (
            <p className="mt-1 max-w-2xl text-xs leading-relaxed text-cinza">{descricao}</p>
          )}
        </div>
        {acao}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}
